import React, { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import axiosInstance from "../../../axiosInstance";
import { useAuth } from "../../../contexts/AuthContext";

const CustomCombinationModal = ({
  isOpen,
  onClose,
  selectedProduct,
  onSuccess,
}) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [selectedValues, setSelectedValues] = useState({});
  const [skuCode, setSkuCode] = useState("");
  const [hsnCode, setHsnCode] = useState("");
  const [existingCombinations, setExistingCombinations] = useState([]);
  const [specSearch, setSpecSearch] = useState("");
  const [combinationName, setCombinationName] = useState("");

  const getApiBaseUrl = useCallback(() => {
    const authorities = user?.authorities || [];

    if (authorities.includes("ROLE_ERP-ADMIN")) {
      return process.env.REACT_APP_API_ERP_ADMIN_URL;
    }
    if (authorities.includes("ROLE_INVENTORY-MANAGER")) {
      return process.env.REACT_APP_API_INV_MANAGER_URL;
    }
    if (authorities.includes("ROLE_CRM-ADMIN")) {
      return process.env.REACT_APP_API_CRM_ADMIN_URL;
    }

    return process.env.REACT_APP_API_INV_MANAGER_URL;
  }, [user?.authorities]);

  const fetchExistingCombinations = useCallback(async () => {
    if (!selectedProduct?.id) return;

    try {
      setLoading(true);
      const baseUrl = getApiBaseUrl();

      const response = await axiosInstance.get(
        `${baseUrl}/products/pricing/${selectedProduct.id}/combinations`,
      );

      setExistingCombinations(response.data?.data || []);
    } catch (error) {
      console.error("Error fetching combinations:", error);
      setExistingCombinations([]);
    } finally {
      setLoading(false);
    }
  }, [selectedProduct?.id, getApiBaseUrl]);

  useEffect(() => {
    if (isOpen && selectedProduct?.id) {
      fetchExistingCombinations();
    } else {
      setSelectedValues({});
      setSkuCode("");
      setHsnCode("");
      setCombinationName("");
      setExistingCombinations([]);
      setSpecSearch("");
    }
  }, [isOpen, selectedProduct?.id, fetchExistingCombinations]);

  const handleSpecValueSelect = (specName, value) => {
    setSelectedValues((prev) => {
      const currentValue = prev[specName];

      // If clicking the same value, unselect it
      if (currentValue?.id === value.id) {
        const newValues = { ...prev };
        delete newValues[specName];
        return newValues;
      }

      // Otherwise, select the new value
      return {
        ...prev,
        [specName]: value,
      };
    });
  };

  const deleteCombination = async (combinationId) => {
    // Guard: invalid / unsaved combination
    if (!Number.isInteger(combinationId) || combinationId <= 0) {
      toast.error("Invalid combination. Please refresh pricing data.");
      return;
    }

    try {
      setLoading(true);
      const baseUrl = getApiBaseUrl();

      await axiosInstance.delete(
        `${baseUrl}/product-combinations/${combinationId}`,
      );

      toast.success("Combination deleted successfully");

      // Refresh the combinations list
      await fetchExistingCombinations();

      // Notify parent (Products page) to refresh pricing cache
      if (typeof onSuccess === "function") {
        onSuccess();
      }
    } catch (error) {
      console.error("Error deleting combination:", error);
      toast.error("Failed to delete combination");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!skuCode.trim()) {
      toast.error("SKU Code is required");
      return;
    }

    const specs = selectedProduct?.specifications || [];
    const selectedSpecs = specs.filter((s) => selectedValues[s.specName]);

    if (selectedSpecs.length === 0) {
      toast.error("Select at least one specification");
      return;
    }

    try {
      setLoading(true);
      const baseUrl = getApiBaseUrl();

      const payload = {
        id: 0,
        productId: selectedProduct.id,
        combinationName: combinationName || null, // send as-is
        skuCode: skuCode.trim(),
        hsnCode: hsnCode.trim() || "",
        specifications: selectedSpecs.map((spec) => ({
          id: spec.id,
          specName: spec.specName,
          values: [
            {
              id: selectedValues[spec.specName].id,
              value: selectedValues[spec.specName].value,
            },
          ],
        })),
      };

      await axiosInstance.post(`${baseUrl}/product-combinations`, payload);

      toast.success("Combination created successfully");

      // Refresh combinations
      await fetchExistingCombinations();

      // Reset form
      setSelectedValues({});
      setSkuCode("");
      setHsnCode("");
      setCombinationName("");

      onSuccess();
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to create combination",
      );
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !selectedProduct) return null;

  const specs = selectedProduct.specifications || [];

  // Filter specs based on search
  const filteredSpecs = specs.filter(
    (spec) =>
      spec.specName.toLowerCase().includes(specSearch.toLowerCase()) ||
      spec.values.some((val) =>
        val.value.toLowerCase().includes(specSearch.toLowerCase()),
      ),
  );

  return (
    <div className="pricing-modal-overlay">
      <div
        className="pricing-modal"
        style={{
          maxWidth: "1200px",
          maxHeight: "92vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          borderRadius: "12px",
          boxShadow: "0 10px 40px rgba(0,0,0,0.15)",
        }}
      >
        {/* HEADER - Fixed */}
        <div
          className="pricing-modal-header"
          style={{
            flexShrink: 0,
            background: "#fff",
            padding: "16px 20px",
          }}
        >
          <div className="pricing-modal-title">
            <h4
              style={{
                fontSize: "17px",
                margin: 0,
                color: "black",
                fontWeight: "600",
              }}
            >
              Add New Combination – {selectedProduct.name}
            </h4>
            <button
              className="close-button"
              onClick={onClose}
              style={{
                fontSize: "28px",
                background: "transparent",
                border: "none",
                cursor: "pointer",
                lineHeight: "1",
                padding: "0",
                width: "32px",
                height: "32px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "50%",
                transition: "background 0.2s ease",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.background = "rgba(255,255,255,0.2)")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.background = "transparent")
              }
            >
              ×
            </button>
          </div>
        </div>

        {/* BODY - Two Column Layout */}
        <div
          className="modal-body"
          style={{
            padding: "20px",
            overflowY: "auto",
            flex: 1,
            background: "#f8f9fa",
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "20px",
              height: "100%",
            }}
          >
            {/* LEFT COLUMN - New Combination */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                background: "#fff",
                borderRadius: "10px",
                padding: "20px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              }}
            >
              <div
                style={{
                  fontSize: "15px",
                  fontWeight: "700",
                  marginBottom: "16px",
                  color: "#2d3748",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <span
                  style={{
                    width: "4px",
                    height: "20px",
                    background: "#3b82f6",
                    borderRadius: "2px",
                  }}
                ></span>
                Create New Combination
              </div>
              <input
                type="text"
                value={combinationName}
                onChange={(e) => setCombinationName(e.target.value)}
                placeholder="Combination Name (Optional)"
                style={{
                  padding: "10px 14px",
                  fontSize: "13px",
                  borderRadius: "8px",
                  border: "2px solid #e2e8f0",
                  transition: "border-color 0.2s ease",
                  outline: "none",
                  marginBottom: "10px",
                }}
                onFocus={(e) => (e.currentTarget.style.borderColor = "#667eea")}
                onBlur={(e) => (e.currentTarget.style.borderColor = "#e2e8f0")}
              />

              {/* Search Bar */}
              <div style={{ position: "relative", marginBottom: "12px" }}>
                {/* INPUTS */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "12px",
                    marginBottom: "10px",
                  }}
                >
                  <input
                    type="text"
                    value={skuCode}
                    onChange={(e) => setSkuCode(e.target.value)}
                    placeholder="SKU Code *"
                    style={{
                      padding: "10px 14px",
                      fontSize: "13px",
                      borderRadius: "8px",
                      border: "2px solid #e2e8f0",
                      transition: "border-color 0.2s ease",
                      outline: "none",
                    }}
                    onFocus={(e) =>
                      (e.currentTarget.style.borderColor = "#667eea")
                    }
                    onBlur={(e) =>
                      (e.currentTarget.style.borderColor = "#e2e8f0")
                    }
                  />

                  <input
                    type="text"
                    value={hsnCode}
                    onChange={(e) => setHsnCode(e.target.value)}
                    placeholder="HSN Code (Optional)"
                    style={{
                      padding: "10px 14px",
                      fontSize: "13px",
                      borderRadius: "8px",
                      border: "2px solid #e2e8f0",
                      transition: "border-color 0.2s ease",
                      outline: "none",
                    }}
                    onFocus={(e) =>
                      (e.currentTarget.style.borderColor = "#667eea")
                    }
                    onBlur={(e) =>
                      (e.currentTarget.style.borderColor = "#e2e8f0")
                    }
                  />
                </div>
                <input
                  type="text"
                  value={specSearch}
                  onChange={(e) => setSpecSearch(e.target.value)}
                  placeholder="🔍 Search specifications or values..."
                  style={{
                    width: "100%",
                    padding: "10px 14px",
                    fontSize: "13px",
                    borderRadius: "8px",
                    border: "2px solid #e2e8f0",
                    transition: "border-color 0.2s ease",
                    outline: "none",
                  }}
                  onFocus={(e) =>
                    (e.currentTarget.style.borderColor = "#667eea")
                  }
                  onBlur={(e) =>
                    (e.currentTarget.style.borderColor = "#e2e8f0")
                  }
                />
              </div>

              {/* SPECIFICATIONS - Scrollable */}
              <div
                style={{
                  flex: 1,
                  overflowY: "auto",
                  border: "2px solid #e2e8f0",
                  borderRadius: "8px",
                  padding: "12px",
                  backgroundColor: "#fafbfc",
                }}
              >
                {filteredSpecs.length === 0 ? (
                  <div
                    style={{
                      fontSize: "14px",
                      color: "#718096",
                      textAlign: "center",
                      padding: "40px 20px",
                    }}
                  >
                    {specSearch
                      ? "No specifications match your search"
                      : "No specifications available"}
                  </div>
                ) : (
                  filteredSpecs.map((spec, index) => (
                    <div
                      key={spec.id}
                      style={{
                        borderBottom:
                          index !== filteredSpecs.length - 1
                            ? "1px solid #e2e8f0"
                            : "none",
                      }}
                    >
                      {/* Spec Name */}
                      <div
                        style={{
                          fontSize: "13px",
                          fontWeight: "700",
                          color: "#4a5568",
                          marginBottom: "10px",
                          letterSpacing: "0.5px",
                        }}
                      >
                        {spec.specName}
                      </div>

                      {/* Value Buttons */}
                      <div
                        style={{
                          display: "flex",
                          gap: "8px",
                          flexWrap: "wrap",
                        }}
                      >
                        {spec.values.map((val) => {
                          const selected =
                            selectedValues[spec.specName]?.id === val.id;

                          return (
                            <button
                              key={val.id}
                              type="button"
                              onClick={() =>
                                handleSpecValueSelect(spec.specName, val)
                              }
                              style={{
                                padding: "8px 16px",
                                fontSize: "12px",
                                fontWeight: "600",
                                borderRadius: "20px",
                                border: selected
                                  ? "2px solid #667eea"
                                  : "2px solid #e2e8f0",
                                backgroundColor: selected ? "#3b82f6" : "#fff",
                                color: selected ? "#fff" : "#4a5568",
                                cursor: "pointer",
                                transition: "all 0.2s ease",
                                boxShadow: selected
                                  ? "0 4px 12px rgba(102,126,234,0.3)"
                                  : "none",
                              }}
                              onMouseEnter={(e) => {
                                if (!selected) {
                                  e.currentTarget.style.backgroundColor =
                                    "#f7fafc";
                                  e.currentTarget.style.borderColor = "#cbd5e0";
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (!selected) {
                                  e.currentTarget.style.backgroundColor =
                                    "#fff";
                                  e.currentTarget.style.borderColor = "#e2e8f0";
                                }
                              }}
                            >
                              {val.value}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* RIGHT COLUMN - Existing Combinations */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                background: "#fff",
                borderRadius: "10px",
                padding: "20px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              }}
            >
              <div
                style={{
                  fontSize: "15px",
                  fontWeight: "700",
                  marginBottom: "16px",
                  color: "#2d3748",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <span
                    style={{
                      width: "4px",
                      height: "20px",
                      background: " #46caeb",
                      borderRadius: "2px",
                    }}
                  ></span>
                  Existing Combinations
                </div>
                <span
                  style={{
                    background: " #46caeb",
                    color: "#fff",
                    padding: "4px 12px",
                    borderRadius: "12px",
                    fontSize: "12px",
                    fontWeight: "700",
                  }}
                >
                  {existingCombinations.length}
                </span>
              </div>

              <div
                style={{
                  flex: 1,
                  overflowY: "auto",
                  border: "2px solid #e2e8f0",
                  borderRadius: "8px",
                  padding: "12px",
                  backgroundColor: "#fafbfc",
                }}
              >
                {existingCombinations.length === 0 ? (
                  <div
                    style={{
                      fontSize: "14px",
                      color: "#718096",
                      textAlign: "center",
                      padding: "40px 20px",
                    }}
                  >
                    No existing combinations yet
                  </div>
                ) : (
                  existingCombinations.map((combo) => (
                    <div
                      key={combo.combinationId}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        padding: "14px",
                        border: "2px solid #e2e8f0",
                        borderRadius: "10px",
                        marginBottom: "12px",
                        fontSize: "12px",
                        backgroundColor: "#fff",
                        transition: "all 0.2s ease",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.04)",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = "#cbd5e0";
                        e.currentTarget.style.boxShadow =
                          "0 4px 12px rgba(0,0,0,0.08)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = "#e2e8f0";
                        e.currentTarget.style.boxShadow =
                          "0 2px 4px rgba(0,0,0,0.04)";
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <div style={{ marginBottom: "8px" }}>
                          <div
                            style={{
                              fontWeight: "700",
                              fontSize: "13px",
                              color: "#2d3748",
                            }}
                          >
                            Combination Name : {combo.combinationName || "-"}
                          </div>

                          <div
                            style={{
                              fontSize: "12px",
                              color: "#64748b",
                              marginTop: "2px",
                            }}
                          >
                            SKU: <strong>{combo.skuCode || "-"}</strong>
                          </div>
                        </div>
                        {combo.hsnCode && (
                          <div
                            style={{
                              color: "#718096",
                              fontSize: "11px",
                              marginBottom: "6px",
                            }}
                          >
                            HSN: {combo.hsnCode}
                          </div>
                        )}
                        <div
                          style={{
                            display: "flex",
                            gap: "6px",
                            flexWrap: "wrap",
                            marginBottom: "8px",
                          }}
                        >
                          {combo.specificationValues?.map((sv, idx) => (
                            <span
                              key={idx}
                              style={{
                                padding: "4px 10px",
                                background: "#46caeb",
                                borderRadius: "12px",
                                fontSize: "11px",
                                color: "#fff",
                                fontWeight: "600",
                              }}
                            >
                              {sv.specificationName}: {sv.value}
                            </span>
                          ))}
                        </div>
                        <div
                          style={{
                            color: "#718096",
                            fontSize: "11px",
                            fontWeight: "600",
                          }}
                        >
                          ₹{combo.costPrice?.toFixed(2) || "0.00"} • Margin:{" "}
                          {combo.marginPercentage?.toFixed(0) || "0"}% • Stock:{" "}
                          {combo.minimumStockQuantity || "0"}
                        </div>
                      </div>

                      <button
                        onClick={() => deleteCombination(combo.combinationId)}
                        disabled={loading}
                        style={{
                          padding: "6px 14px",
                          fontSize: "11px",
                          fontWeight: "700",
                          background: "#ff6692",
                          color: "#fff",
                          border: "none",
                          borderRadius: "16px",
                          cursor: loading ? "not-allowed" : "pointer",
                          opacity: loading ? 0.6 : 1,
                          marginLeft: "12px",
                          flexShrink: 0,
                          transition: "all 0.2s ease",
                        }}
                        onMouseEnter={(e) => {
                          if (!loading) {
                            e.currentTarget.style.transform =
                              "translateY(-2px)";
                            e.currentTarget.style.boxShadow =
                              "0 4px 12px rgba(245,87,108,0.4)";
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = "translateY(0)";
                          e.currentTarget.style.boxShadow =
                            "0 2px 8px rgba(245,87,108,0.3)";
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>

        {/* FOOTER - Fixed */}
        <div
          style={{
            padding: "16px 20px",
            borderTop: "2px solid #e2e8f0",
            display: "flex",
            justifyContent: "flex-end",
            gap: "12px",
            backgroundColor: "#fff",
            flexShrink: 0,
          }}
        >
          {/* Cancel Button */}
          <button
            onClick={onClose}
            style={{
              padding: "10px 24px",
              fontSize: "14px",
              fontWeight: "600",
              background: "#ff6692",
              color: "#fff",
              border: "none",
              borderRadius: "20px",
              cursor: "pointer",
              transition: "background 0.2s ease, transform 0.2s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "#e65c83"; // 10% darker
              e.currentTarget.style.transform = "translateY(-1px)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "#ff6692";
              e.currentTarget.style.transform = "translateY(0)";
            }}
          >
            Cancel
          </button>

          {/* Submit Button */}
          <button
            onClick={handleSubmit}
            disabled={loading}
            style={{
              padding: "10px 24px",
              fontSize: "14px",
              fontWeight: "600",
              background: loading ? "#cbd5e0" : "#36c76c",
              color: "#fff",
              border: "none",
              borderRadius: "20px",
              cursor: loading ? "not-allowed" : "pointer",
              transition: "background 0.2s ease, transform 0.2s ease",
            }}
            onMouseEnter={(e) => {
              if (!loading) {
                e.currentTarget.style.background = "#31b463"; // 10% darker
                e.currentTarget.style.transform = "translateY(-1px)";
              }
            }}
            onMouseLeave={(e) => {
              if (!loading) {
                e.currentTarget.style.background = "#36c76c";
                e.currentTarget.style.transform = "translateY(0)";
              }
            }}
          >
            {loading ? "Saving..." : "Create Combination"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CustomCombinationModal;
